"use client";

import { ImageGallery, ImageGalleryCategory, ImageGalleryImage, useImageGallery } from "@/components/common/gallery";
import { GalleryCategories, GalleryImageCounter, GalleryNavigationButtons } from "@/components/common/gallery/overlays";
import { prepareGalleryImages } from "@/utils/sup/gallery";
import * as React from "react";
import { type GalleryProps, getSortedCategories } from "./shared";

// Re-export GalleryProps for external consumers
export type { GalleryProps };

// Internal component that uses gallery context to sync category
function GalleryContent({
	images,
	sortedCategories,
	selectedCategory,
	setSelectedCategory,
}: {
	images: ImageGalleryImage[];
	sortedCategories: ImageGalleryCategory[];
	selectedCategory: ImageGalleryCategory | undefined;
	setSelectedCategory: (category: ImageGalleryCategory | undefined) => void;
}) {
	const { currentIndex, goToPrevious, goToNext } = useImageGallery();

	// Sync selected category with current image
	React.useEffect(() => {
		const currentImage = images[currentIndex];
		if (currentImage?.space) {
			const currentCategory = sortedCategories.find(cat => cat.code === currentImage.space);
			if (currentCategory && currentCategory.code !== selectedCategory?.code) {
				setSelectedCategory(currentCategory);
			}
		}
	}, [currentIndex, images, sortedCategories, selectedCategory, setSelectedCategory]);

	const showCategories = sortedCategories.length > 0;
	const canGoPrevious = currentIndex > 0;
	const canGoNext = currentIndex < images.length - 1;

	return (
		<>
			{/* Image Counter - hidden when category nav is visible */}
			{!showCategories && <GalleryImageCounter showImageName={true} />}

			{/* Navigation Buttons */}
			<GalleryNavigationButtons
				onPrevious={goToPrevious}
				onNext={goToNext}
				canGoPrevious={canGoPrevious}
				canGoNext={canGoNext}
			/>

			{/* Category Navigation */}
			{showCategories && (
				<GalleryCategories
					categories={sortedCategories}
					selectedCategory={selectedCategory}
					onCategoryChange={setSelectedCategory}
				/>
			)}
		</>
	);
}

export const GalleryLarge = React.memo(function GalleryLarge({
	mediaList = [],
	categories,
	coverImage,
	projectId,
	type,
	className,
	isLoading = false,
	shouldPrioritizeCoverImage = true,
	sizes = "80dvw",
	onMainImageClick,
	videoUrl,
	videoCaptionsUrl,
}: GalleryProps) {
	// Convert to ImageGalleryImage format and apply sorting
	const sortedImages = React.useMemo(() => {
		const prepared = prepareGalleryImages({
			mediaList,
			coverImage,
			projectId,
			type,
			shouldPrioritizeCoverImage,
		});
		// Prepend video as the first slide if provided
		if (videoUrl) {
			prepared.unshift({
				url: videoUrl,
				alt: "Video",
				name: "Video",
				space: "videos",
				isVideo: true,
				videoCaptionsSrc: videoCaptionsUrl,
				showCaptionsByDefault: !!videoCaptionsUrl,
			});
		}
		return prepared;
	}, [mediaList, coverImage, projectId, type, shouldPrioritizeCoverImage, videoUrl, videoCaptionsUrl]);

	// Sort categories by unitSpaces priority and filter to only those with images
	const sortedCategories = React.useMemo(() => {
		return getSortedCategories(categories, sortedImages);
	}, [categories, sortedImages]);

	// Track selected category - select the first category from sorted list
	const [selectedCategory, setSelectedCategory] = React.useState<ImageGalleryCategory | undefined>(sortedCategories[0]);

	if (isLoading) {
		return (
			<div className="w-full h-full bg-gray-100 animate-pulse">
				<div className="w-full h-full bg-gray-200"></div>
			</div>
		);
	}

	if (!sortedImages || sortedImages.length === 0) {
		return (
			<div className="flex items-center justify-center w-full h-96 bg-gray-100">
				<div className="text-center">
					<div className="w-16 h-16 bg-gray-300 mx-auto mb-4" />
					<p className="text-gray-500">No images to show</p>
				</div>
			</div>
		);
	}

	return (
		<div className={`bg-white overflow-hidden h-full relative ${className || ""}`}>
			<ImageGallery
				images={sortedImages}
				className="w-full h-full"
				showNavigationButtons={true}
				showImageCounter={false} // We'll use custom counter
				enableKeyboardNavigation={true}
				enableDoubleTapFullscreen={true}
				autoLoop={true}
				sizes={sizes}
				onMainImageClick={onMainImageClick}
			>
				<GalleryContent
					images={sortedImages}
					sortedCategories={sortedCategories}
					selectedCategory={selectedCategory}
					setSelectedCategory={setSelectedCategory}
				/>
			</ImageGallery>
		</div>
	);
});
